import PurchaserEmailRow from '@/components/manage/travel-passes/PurchaserEmailRow';
import { Form } from '@/components/primitives/Form';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import { sendTicketsToEmailLoadingSelector } from '@/features/loading/loadingSelectors';
import { sendTicketsToEmail } from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { SendToEmailFormValues } from '@/types/sendTicketsToEmail';
import { breakpoints } from '@/utils/breakpoints';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { z } from 'zod';

interface SendTravelPassToEmailModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  holderName: string;
  defaultEmail: string;
}

const createSendTravelPassToEmailSchema = (t: (key: string) => string) => {
  return z.object({
    bookingId: z.string(),
    passengers: z.array(z.any()).optional(),
    purchaser: z
      .object({
        sendSms: z.boolean(),
        sendEmail: z.boolean(),
        emailOverride: z
          .array(z.string().min(1).email(t('text.invalidEmail')))
          .min(1),
        phoneNumberOverride: z.array(z.string()).optional(),
      })
      .nullable(),
    includeTaxInvoices: z.boolean().optional(),
    _meta: z.object({
      sendAllTickets: z.boolean(),
    }),
  });
};

const SendTravelPassToEmailModal: FC<SendTravelPassToEmailModalProps> = ({
  isOpen,
  onOpenChange,
  holderName,
  defaultEmail,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const booking = useSelector(manageFlowBookingSelector)!;
  const loading = useSelector(sendTicketsToEmailLoadingSelector);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const defaultValues = useMemo<SendToEmailFormValues>(() => {
    return {
      bookingId: booking.id!,
      passengers: [],
      purchaser: {
        sendSms: false,
        sendEmail: true,
        emailOverride: [defaultEmail || ''],
      },
      includeTaxInvoices: false,
      _meta: {
        sendAllTickets: false,
      },
    };
  }, [booking.id, defaultEmail]);

  const form = useForm<SendToEmailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(createSendTravelPassToEmailSchema(t)),
    defaultValues,
  });

  const handleClose = useCallback(() => {
    onOpenChange(false);
    form.reset(defaultValues);
  }, [onOpenChange, form, defaultValues]);

  const onSubmit = useCallback(
    async ({ _meta, ...payload }: SendToEmailFormValues) => {
      await dispatch(sendTicketsToEmail(payload))
        .unwrap()
        .then(() => {
          toast.success(t('text.travelPassSentSuccessfully'));
          handleClose();
        });
    },
    [dispatch, handleClose, t]
  );

  const formId = useMemo(() => 'send-travel-pass-to-email-form', []);

  return (
    <FormProvider {...form}>
      <Form form={form} onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <ResponsiveDialog
          open={isOpen}
          onOpenChange={onOpenChange}
          title={<TransText i18nKey="sendToEmail" />}
          content={
            <div className="flex flex-col gap-4 p-6">
              <Typography variant="subtitle">
                <TransText i18nKey="sendTravelPassTo" />:
              </Typography>
              <PurchaserEmailRow
                holderName={holderName}
                defaultEmail={defaultEmail}
              />
            </div>
          }
          footer={
            <div className="flex w-full gap-2 laptop:flex-row laptop:justify-end">
              <Button
                variant="tertiary"
                type="button"
                onClick={handleClose}
                isDisabled={loading}
                data-testid="close-modal-button"
                size={isLaptopOrBigger ? 'regular' : 'large'}
                className="rounded-lg laptop:w-auto"
              >
                <TransText i18nKey="cancel" />
              </Button>
              <Button
                type="submit"
                isDisabled={loading}
                data-testid="send-email-button"
                form={formId}
                size={isLaptopOrBigger ? 'regular' : 'large'}
                className="rounded-lg laptop:w-auto"
              >
                <Icons.send width={20} height={20} />
                <TransText i18nKey="send" />
              </Button>
            </div>
          }
        />
      </Form>
    </FormProvider>
  );
};

export default SendTravelPassToEmailModal;
